import { describeError, useApi } from '@classgo/app-kit';
import type { ChargeView, PaymentView } from '@classgo/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  Table,
  useToast,
} from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { billingApi, billingQueries } from '../../billing/api';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import { formatMoney, today } from '../../lib/money';

/**
 * The electronic invoice of a charge, once the API carries one (ticket #110, not shipped: the
 * provider account is missing). `ChargeView` has no invoice field yet, so this reads an optional
 * `invoice` (`status`, `url`) off the row without asserting it exists; the statement's "Factura"
 * column renders only when some row brings one, and stays hidden until then.
 */
const invoiceOf = (row: ChargeView): { status: string; url: string | null } | null => {
  const raw = (row as ChargeView & { invoice?: unknown }).invoice;
  if (!raw || typeof raw !== 'object') return null;
  const { status, url } = raw as { status?: unknown; url?: unknown };
  if (typeof status !== 'string') return null;
  return { status, url: typeof url === 'string' ? url : null };
};

const chargeTone = (status: ChargeView['status']) =>
  status === 'paid'
    ? 'success'
    : status === 'partial'
      ? 'info'
      : status === 'void'
        ? 'gray'
        : 'warning';

/**
 * A family's account statement (ticket #70): charges with balances, payments with allocations,
 * totals to the cent; a manual payment with receipt, and the payment link of a charge to share
 * with the family until the app arrives (M2).
 */
export const FamilyStatementScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const { guardianId = '' } = useParams({ strict: false });
  const statement = useQuery(billingQueries.statement(api, guardianId));
  const [paying, setPaying] = useState<ChargeView | null | 'any'>(null);
  const checkout = useMutation({
    mutationFn: (chargeId: string) => billingApi(api).checkout(chargeId),
    onSuccess: async (intent) => {
      if (intent.checkoutUrl) {
        try {
          await navigator.clipboard.writeText(intent.checkoutUrl);
          toast.show({
            tone: 'success',
            title: t('billing.link.copied'),
            description: t('billing.link.expires', {
              date: formatDate(intent.expiresAt.slice(0, 10)),
            }),
          });
        } catch {
          toast.show({
            tone: 'info',
            title: t('billing.link.title'),
            description: intent.checkoutUrl,
          });
        }
      }
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('billing.link.title'),
        description: describeError(t, error),
      }),
  });
  if (statement.isPending) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (statement.isError) {
    return (
      <EmptyState
        title={t('entity.error.title')}
        description={describeError(t, statement.error)}
        action={
          <Link
            to="/cobros"
            className="text-brand-600 underline underline-offset-2 hover:decoration-2"
          >
            {t('billing.back')}
          </Link>
        }
      />
    );
  }
  const data = statement.data;
  const currency = data.currency;
  const refresh = () => queries.invalidateQueries({ queryKey: ['billing'] });
  return (
    <div className="space-y-4">
      <Link
        to="/cobros"
        className="text-sm text-brand-600 underline underline-offset-2 hover:decoration-2"
      >
        ← {t('billing.back')}
      </Link>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {data.guardian.givenName} {data.guardian.familyName}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{data.guardian.email}</p>
            <p className="mt-1 text-sm text-gray-600">
              {t('billing.statement.students', {
                names:
                  data.students
                    .map((student) =>
                      `${student.givenName ?? ''} ${student.familyName ?? ''}`.trim(),
                    )
                    .join(', ') || t('common.none'),
              })}
            </p>
          </div>
          <Button onClick={() => setPaying('any')}>{t('billing.payment.record')}</Button>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 [&>div:last-child]:col-span-2 sm:grid-cols-5 sm:[&>div:last-child]:col-span-1">
          {(
            [
              ['charged', data.totals.charged],
              ['discounts', data.totals.discounts],
              ['paid', data.totals.paid],
              ['credit', data.totals.credit],
              ['balance', data.totals.balance],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className="rounded-control bg-gray-50 p-3">
              <dt className="text-xs text-gray-500">{t(`billing.statement.totals.${key}`)}</dt>
              <dd
                className={`text-lg font-semibold ${key === 'balance' && value > 0 ? 'text-danger-700' : 'text-gray-900'}`}
              >
                {formatMoney(value, currency)}
              </dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card title={t('billing.statement.charges')} flush>
        <Table<ChargeView>
          caption={t('billing.statement.charges')}
          rows={data.charges}
          rowKey={(row) => row.id}
          empty={<EmptyState title={t('billing.statement.charges.empty')} description="" />}
          columns={[
            {
              key: 'description',
              header: t('billing.statement.column.description'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">{row.description}</p>
                  <p className="text-xs text-gray-500">
                    {row.studentName ?? ''}
                    {row.periodLabel ? ` · ${row.periodLabel}` : ''}
                  </p>
                </div>
              ),
            },
            {
              key: 'dueOn',
              header: t('billing.statement.column.dueOn'),
              cell: (row) => formatDate(row.dueOn),
            },
            {
              key: 'amount',
              header: t('billing.statement.column.amount'),
              align: 'right',
              cell: (row) => formatMoney(row.amount, currency),
            },
            {
              key: 'discount',
              header: t('billing.statement.column.discount'),
              align: 'right',
              cell: (row) =>
                row.discountAmount > 0
                  ? formatMoney(row.discountAmount, currency)
                  : t('common.none'),
            },
            {
              key: 'balance',
              header: t('billing.statement.column.balance'),
              align: 'right',
              cell: (row) => (
                <span className={row.balance > 0 ? 'font-medium text-gray-900' : 'text-gray-500'}>
                  {formatMoney(row.balance, currency)}
                </span>
              ),
            },
            {
              key: 'status',
              header: t('billing.statement.column.status'),
              cell: (row) => (
                <Badge tone={chargeTone(row.status)}>
                  {t(`billing.charge.status.${row.status}`)}
                </Badge>
              ),
            },
            ...(data.charges.some((row) => invoiceOf(row) !== null)
              ? [
                  {
                    key: 'invoice',
                    header: t('billing.statement.column.invoice'),
                    cell: (row: ChargeView) => {
                      const invoice = invoiceOf(row);
                      if (!invoice) return t('common.none');
                      return invoice.url ? (
                        <a
                          className="underline underline-offset-2 hover:decoration-2"
                          href={invoice.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {invoice.status}
                        </a>
                      ) : (
                        invoice.status
                      );
                    },
                  },
                ]
              : []),
            {
              key: 'actions',
              header: '',
              cell: (row) =>
                row.balance > 0 && row.status !== 'void' ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setPaying(row)}>
                      {t('billing.payment.record')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => checkout.mutate(row.id)}
                      disabled={checkout.isPending}
                    >
                      {t('billing.link.copy')}
                    </Button>
                  </div>
                ) : null,
            },
          ]}
        />
      </Card>
      <Card title={t('billing.statement.payments')} flush>
        <Table<PaymentView>
          caption={t('billing.statement.payments')}
          rows={data.payments}
          rowKey={(row) => row.id}
          empty={<EmptyState title={t('billing.statement.payments.empty')} description="" />}
          columns={[
            {
              key: 'paidOn',
              header: t('billing.statement.column.paidOn'),
              cell: (row) => formatDate(row.paidOn),
            },
            {
              key: 'method',
              header: t('billing.statement.column.method'),
              cell: (row) => t(`billing.method.${row.method}`),
            },
            {
              key: 'reference',
              header: t('billing.statement.column.reference'),
              cell: (row) => row.receiptNumber ?? row.reference ?? t('common.none'),
            },
            {
              key: 'amount',
              header: t('billing.statement.column.amount'),
              align: 'right',
              cell: (row) => formatMoney(row.amount, currency),
            },
            {
              key: 'allocated',
              header: t('billing.statement.column.allocated'),
              align: 'right',
              cell: (row) => formatMoney(row.allocatedAmount, currency),
            },
          ]}
        />
      </Card>
      <Modal
        open={paying !== null}
        onClose={() => setPaying(null)}
        title={t('billing.payment.record')}
      >
        {paying !== null ? (
          <PaymentForm
            guardianId={guardianId}
            currency={currency}
            charge={paying === 'any' ? null : paying}
            onDone={() => {
              setPaying(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

const PaymentForm = ({
  guardianId,
  currency,
  charge,
  onDone,
}: {
  guardianId: string;
  currency: string;
  charge: ChargeView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const [amount, setAmount] = useState(charge ? String(charge.balance) : '');
  const [paidOn, setPaidOn] = useState(today());
  const [method, setMethod] = useState<'cash' | 'transfer'>('cash');
  const [reference, setReference] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const record = useMutation({
    mutationFn: () =>
      billingApi(api).recordPayment({
        guardianId,
        amount: Number(amount),
        paidOn,
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(receiptNumber.trim() ? { receiptNumber: receiptNumber.trim() } : {}),
        ...(charge
          ? {
              allocations: [
                { chargeId: charge.id, amount: Math.min(Number(amount), charge.balance) },
              ],
            }
          : {}),
      }),
    onSuccess: onDone,
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        record.mutate();
      }}
    >
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      {charge ? (
        <p className="text-sm text-gray-600">
          {t('billing.payment.for', {
            description: charge.description,
            balance: formatMoney(charge.balance, currency),
          })}
        </p>
      ) : (
        <p className="text-sm text-gray-600">{t('billing.payment.oldestFirst')}</p>
      )}
      <Input
        label={t('billing.payment.amount')}
        type="number"
        min={1}
        step={1}
        required
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
      />
      <Input
        label={t('billing.payment.paidOn')}
        type="date"
        required
        value={paidOn}
        onChange={(event) => setPaidOn(event.target.value)}
      />
      <Select
        label={t('billing.payment.method')}
        value={method}
        onChange={(event) => setMethod(event.target.value as 'cash' | 'transfer')}
        options={[
          { value: 'cash', label: t('billing.method.cash') },
          { value: 'transfer', label: t('billing.method.transfer') },
        ]}
      />
      <Input
        label={t('billing.payment.receipt')}
        help={t('billing.payment.receipt.help')}
        value={receiptNumber}
        onChange={(event) => setReceiptNumber(event.target.value)}
      />
      <Input
        label={t('billing.payment.reference')}
        value={reference}
        onChange={(event) => setReference(event.target.value)}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={record.isPending}>
          {t('billing.payment.submit')}
        </Button>
      </div>
    </form>
  );
};
